import { ApplicationConfig, isDevMode, ErrorHandler, importProvidersFrom, APP_INITIALIZER, Injector, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withPreloading } from '@angular/router';
import { CustomPreloadingStrategy } from './preloading-strategy';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideStore, StoreModule , Store } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { providePrimeNG } from 'primeng/config';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MessageService, ConfirmationService } from 'primeng/api';
import { LocationStrategy, HashLocationStrategy, registerLocaleData, DatePipe } from '@angular/common';
import { AlertErrorHandler } from '@fitness-force/errors';;

import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { ApplicationInitializerFactory } from '@fitness-force/shared-utils';

import Lara from '@primeng/themes/lara';
import { appointmentTypeReducer, authReducer, cityListReducer, claimsReducer, classTypeReducer,
  companyReducer, countryListReducer, locationReducer, roomReducer, serviceTypeReducer, sourceStatsReducer, staffNotificationReducer, staffReducer, stageReducer,
  stateListReducer, StatsigReducer, TenantReducer, tenantReducer, themeReducer,
  trialMasterReducer, CompanyEffects , ThemeEffects } from '@fitness-force/state';
  import { routerReducer } from '@ngrx/router-store';


import localeAr from '@angular/common/locales/ar';
import { CHUNK_LOAD_RECOVERY, createBrowserChunkLoadRecovery } from './core/i18n/chunk-load-recovery';
import { LazyTranslateLoader } from './core/i18n/lazy-translate.loader';
import { provideAnimations } from '@angular/platform-browser/animations';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { environment } from '@fitness-force/environments';
import { IntercepterService } from '@fitness-force/api';
import { NGX_ECHARTS_CONFIG } from 'ngx-echarts';
import { STATSIG_INIT_CONFIG, StatsigModule } from '@statsig/angular-bindings';
import { GoogleLoginProvider, SocialAuthServiceConfig } from '@abacritt/angularx-social-login';


registerLocaleData(localeAr);


export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withPreloading(CustomPreloadingStrategy)),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: STATSIG_INIT_CONFIG,
      useFactory: () => ({
        sdkKey: environment.STATSIG_SDK_KEY,
        user: { userID: 'anonymous' }, // Initialize with anonymous user, will be updated after login
        options: {
          environment: { tier: environment.ENVIRONMENT_NAME },
          initTimeoutMs: 3000,
        }
      })
    },
    importProvidersFrom(StatsigModule),
    provideStore(),
    provideEffects([CompanyEffects, ThemeEffects]),
    provideAnimations(),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
    }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useClass: LazyTranslateLoader,
      },
      fallbackLang: 'en',
      lang: 'en'
    }),
    {
      provide: CHUNK_LOAD_RECOVERY,
      useFactory: createBrowserChunkLoadRecovery,
    },
    importProvidersFrom(
      StoreModule.forRoot({
        company: companyReducer,
        claims: claimsReducer,
        tenants: tenantReducer,
        countries: countryListReducer,
        staff: staffReducer,
        router: routerReducer,
        stages: stageReducer,
        appointmentTypes: appointmentTypeReducer,
        classTypes: classTypeReducer,
        states: stateListReducer,
        cities: cityListReducer,
        serviceTypeList: serviceTypeReducer,
        rooms: roomReducer,
        location: locationReducer,
        tenant: TenantReducer,
        auth: authReducer,
        statsigReducer: StatsigReducer,
        staffPreferences: staffNotificationReducer,
        theme: themeReducer,
        trials: trialMasterReducer,
        sourceStats: sourceStatsReducer
      }),
    ),
    DatePipe,
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    MessageService,
    ConfirmationService,
    { provide: HTTP_INTERCEPTORS, useClass: IntercepterService, multi: true },
    { provide: ErrorHandler, useClass: AlertErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: ApplicationInitializerFactory,
      deps: [TranslateService, Injector, Store],
      multi: true
    },

    importProvidersFrom(
      GoogleTagManagerModule.forRoot({
      id: environment.googleGTM,
    }),
    ),
    providePrimeNG({
      ripple: true,
      inputVariant: 'outlined',
      theme: {
        preset: Lara,
        options: {
            prefix: 'p',
            darkModeSelector: 'system',
            cssLayer: false
        }
    },
      zIndex: {
        modal: 1100,
        overlay: 1200,
        menu: 1000,
        tooltip: 1100
      },
    }),
    {
      provide: NGX_ECHARTS_CONFIG,
      useValue: {
        echarts: () => import('echarts')
      }
    },
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(
              environment.GOOGLE_AUTH_ID
            )
          }
        ],
        onError: (err: any) => {
          console.error(err);
        }
      } as SocialAuthServiceConfig,
    }
  ]
}; 